import { useMutation } from "@apollo/client";
import {
  Button,
  Group,
  Input,
  LoadingOverlay,
  PasswordInput,
  ScrollArea,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy } from "@tabler/icons";
import { CREATE_LOAN_PROVIDER } from "apollo/mutuations";
import { LOAN_PROVIDERS } from "apollo/queries";
import { customLoader } from "components/utilities/loader";

const LoanProviderAddModal = ({ setOpened, size, activePage, refetch }) => {
  const form = useForm({
    initialValues: {
      name: "",
      code: "",
      user_phone: "",
      user_email: "",
      user_password: "",
      description: "",
      contact_email: "",
      contact_phone: "",
      website: "",
      api_base_url: "",
    },
    validate: {
      name: (value) => (value ? null : "Name is required"),
      code: (value) => (value ? null : "Code is required"),
      user_phone: (value) => {
        if (!value) return "User phone is required";
        if (!/^\d{8}$/.test(value)) return "Phone must be 8 digits";
        return null;
      },
      user_email: (value) => {
        if (!value) return "User email is required";
        if (!/^\S+@\S+$/.test(value)) return "Invalid email";
        return null;
      },
      user_password: (value) => {
        if (!value) return "User password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        return null;
      },
      contact_email: (value) =>
        value && !/^\S+@\S+$/.test(value) ? "Invalid email" : null,
      contact_phone: (value) =>
        value && !/^\d{8}$/.test(value) ? "Phone must be 8 digits" : null,
      website: (value) =>
        value && !/^https?:\/\/.+/.test(value)
          ? "Invalid URL (must start with http:// or https://)"
          : null,
      api_base_url: (value) =>
        value && !/^https?:\/\/.+/.test(value)
          ? "Invalid URL (must start with http:// or https://)"
          : null,
    },
  });

  const [createLoanProvider, { loading }] = useMutation(CREATE_LOAN_PROVIDER, {
    refetchQueries: [
      {
        query: LOAN_PROVIDERS,
        variables: {
          first: parseInt(size),
          page: activePage,
          search: "",
          ordered_by: [
            {
              column: "CREATED_AT",
              order: "DESC",
            },
          ],
        },
      },
    ],
    onCompleted() {
      showNotification({
        color: "green",
        title: "Success",
        message: "Loan Provider Created Successfully",
      });
      setOpened(false);
      form.reset();
      if (refetch) refetch();
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Create Loan Provider",
      });
    },
  });

  const handleSubmit = (values) => {
    const fullUserPhone = `+2519${values.user_phone}`;
    const fullContactPhone = values.contact_phone
      ? `+2519${values.contact_phone}`
      : null;

    createLoanProvider({
      variables: {
        name: values.name,
        code: values.code,
        user_phone: fullUserPhone,
        user_email: values.user_email,
        user_password: values.user_password,
        description: values.description || null,
        contact_email: values.contact_email || null,
        contact_phone: fullContactPhone,
        website: values.website || null,
        api_base_url: values.api_base_url || null,
      },
    });
  };

  return (
    <div style={{ position: "relative", minHeight: "400px" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ margin: 0, marginBottom: "8px" }}>Create Loan Provider</h2>
        <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
          Add a new loan provider to the system. All fields marked with * are
          required.
        </p>
      </div>
      <ScrollArea style={{ height: "70vh" }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            label="Provider Name"
            placeholder="Enter provider name"
            required
            {...form.getInputProps("name")}
            mb="md"
          />

          <TextInput
            label="Provider Code"
            placeholder="Enter provider code"
            required
            {...form.getInputProps("code")}
            mb="md"
          />

          <Input.Wrapper label="User Phone" mb="md" required>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  backgroundColor: "#f1f3f5",
                  border: "1px solid #ced4da",
                  borderRight: "none",
                  padding: "8px 12px",
                  borderRadius: "4px 0 0 4px",
                  color: "#495057",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                +2519
              </div>
              <Input
                placeholder="12345678"
                maxLength={8}
                {...form.getInputProps("user_phone")}
                style={{ flex: 1 }}
                styles={{
                  input: {
                    borderRadius: "0 4px 4px 0",
                  },
                }}
              />
            </div>
            {form.errors.user_phone && (
              <div
                style={{ color: "#fa5252", fontSize: "12px", marginTop: "5px" }}
              >
                {form.errors.user_phone}
              </div>
            )}
          </Input.Wrapper>

          <TextInput
            label="User Email"
            placeholder="user@example.com"
            required
            {...form.getInputProps("user_email")}
            mb="md"
          />

          <PasswordInput
            label="User Password"
            placeholder="Enter password"
            required
            {...form.getInputProps("user_password")}
            mb="md"
          />

          <Textarea
            label="Description"
            placeholder="Enter description"
            minRows={3}
            {...form.getInputProps("description")}
            mb="md"
          />

          <TextInput
            label="Contact Email"
            placeholder="contact@example.com"
            {...form.getInputProps("contact_email")}
            mb="md"
          />

          <Input.Wrapper label="Contact Phone" mb="md">
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  backgroundColor: "#f1f3f5",
                  border: "1px solid #ced4da",
                  borderRight: "none",
                  padding: "8px 12px",
                  borderRadius: "4px 0 0 4px",
                  color: "#495057",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                +2519
              </div>
              <Input
                placeholder="11000001"
                maxLength={8}
                {...form.getInputProps("contact_phone")}
                style={{ flex: 1 }}
                styles={{
                  input: {
                    borderRadius: "0 4px 4px 0",
                  },
                }}
              />
            </div>
            {form.errors.contact_phone && (
              <div
                style={{ color: "#fa5252", fontSize: "12px", marginTop: "5px" }}
              >
                {form.errors.contact_phone}
              </div>
            )}
          </Input.Wrapper>

          <TextInput
            label="Website"
            placeholder="https://www.example.com"
            {...form.getInputProps("website")}
            mb="md"
          />

          <TextInput
            label="API Base URL"
            placeholder="https://api.example.com"
            {...form.getInputProps("api_base_url")}
            mb="md"
          />

          <Group position="right" mt="xl">
            <Button
              type="submit"
              leftIcon={<IconDeviceFloppy size={16} />}
              style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
              disabled={loading}
            >
              Save
            </Button>
          </Group>
        </form>
      </ScrollArea>
    </div>
  );
};

export default LoanProviderAddModal;
